<template>
  <div class="workbook-color-card flex flex-col items-center w-full"
    :class="cardWidth === 'sm' ? 'lg:w-44' : 'lg:w-48'">
    <div v-if="showModelName || showSaveInHeader" class="flex items-center gap-2 mb-1">
      <h3 v-if="showModelName" class="text-sm font-semibold leading-tight m-0">
        {{ model.name }}
      </h3>
      <ModelSaveToggle v-if="showSaveInHeader" :model="model" size="sm" />
    </div>
    <NuxtLink class="w-full" :to="productLink">
      <WorkbookProductImage class="w-full " :class="isPrint ? 'bg-workbook-white' : 'product-display-gradient'" :src="imageSrc" />
    </NuxtLink>

    <div class="my-2 text-xs font-medium text-center">
      <span v-if="isNewColour" class="w-1.5 h-1.5 rotate-45 border border-highlightnew inline-block mr-2"></span>
      <span>{{ color }}</span>
    </div>

    <div v-if="showQuantities" class="w-full px-4 flex flex-col pb-8">
      <div class="flex gap-2 items-center justify-between mb-2" v-if="sizeRows.length > 2">
        <button type="button" class="text-sm underline text-center dark:text-white"
          @click="applyDefaultQuantities">
          Add Default QTY
        </button>
        <UPopover :content="{
          align: 'center',
          side: 'top',
          sideOffset: 8
        }">          
          <UButton icon="i-lucide-columns-3-cog" size="xs" variant="link" />
          <template #content>
            <!-- Popover med default size range -->
            <div class="p-4 flex flex-col">
              <p class="bold mb-3"><strong>Default size range</strong></p>
              <div v-for="size in CANONICAL_ORDER" :key="size" class="flex items-center gap-2 text-sm mb-2">
                <span class="w-9">{{ size }}</span>
                <div class="flex gap-1">
                  <button
                    class="h-5 w-5 flex items-center justify-center bg-workbook-light-grey/20 dark:text-workbook-black text-sm"
                    :disabled="settings.defaultSizeQuantities[size] === 0" @click="decDefaultSize(size)"
                    aria-label="Decrease">-</button>
                  <span class="h-5 w-6 flex items-center justify-center text-sm">
                    {{ settings.defaultSizeQuantities[size] }}
                  </span>
                  <button
                    class="h-5 w-5 flex items-center justify-center bg-workbook-light-grey/20 dark:text-workbook-black text-sm"
                    @click="incDefaultSize(size)" aria-label="Increase">+</button>
                </div>
              </div>
              <button type="button" class="mt-2 text-sm underline text-center dark:text-white"
                @click="resetQuantityDefaults" >
                Reset defaults
              </button>
              
            </div>
          </template>
        </UPopover>
      </div>

      <div v-for="row in sizeRows" :key="row.sku" class="justify-center flex items-center gap-2 text-sm mb-2">
        <span class="w-9">{{ row.displaySize }}</span>
        <div class="flex gap-2 items-center">
          <button class="h-5 w-5 flex items-center justify-center bg-workbook-light-grey/20 dark:text-workbook-black text-sm"
            :disabled="row.qty === 0" @click="dec(row.sku)" aria-label="Decrease">-</button>
          <span class="h-5 w-6 flex items-center justify-center text-sm">
            <!-- Input field for qty with global tabIndex -->
            <input
              type="number"
              :value="row.qty"
              min="0"
              max="999"
              class="w-12 text-center border border-neutral-300 rounded-sm focus:border-primary focus:outline-none"
              :tabindex="getGlobalTabIndexForSku ? getGlobalTabIndexForSku(row.sku) : (idx + 1)"
              @input="onQtyInput(row.sku, $event.target.value)"
              @blur="onQtyBlur(row.sku, $event.target.value)"
              @keydown.enter="onQtyBlur(row.sku, $event.target.value)"
            />
          </span>
          <button class="h-5 w-5 flex items-center justify-center bg-workbook-light-grey/20 dark:text-workbook-black text-sm"
            @click="inc(row.sku)" aria-label="Increase">+</button>
          <button class="h-5 w-5 flex items-center justify-center text-sm" :disabled="row.qty === 0"
            @click="clearVariant(row.sku)" aria-label="Clear">×</button>
        </div>
      </div>
      <div class="w-full flex items-center flex-col">
        <button type="button" class="mt-2 text-sm underline text-center dark:text-white"
          @click="removeColor" :disabled="!hasAnyQty">
          Clear quantities
        </button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ProductModel } from '@/types/catalog'
import { useCartStore } from '@/store/cart'
import ModelSaveToggle from '@/components/cart/ModelSaveToggle.vue'
import { imageForColour } from '@/utils/productImages'
import { useSettingsStore } from '@/store/settings'


const route = useRoute()

const isPrint = computed(() =>
  route.name?.toString().toLowerCase().includes('print') ||
  route.path.toLowerCase().includes('print')
)

const CANONICAL_ORDER = ['XS', 'SM', 'MD', 'LG', 'XL', 'XXL', 'XXXL'] as const
type Canon = typeof CANONICAL_ORDER[number]

const props = withDefaults(defineProps<{
  model: ProductModel
  color: string
  showQuantities?: boolean
  showSaveLabel?: boolean
  showModelName?: boolean
  showSaveInHeader?: boolean
  cardWidth?: 'sm' | 'md'
  colour?: string
  getGlobalTabIndexForSku?: (sku: string) => number
}>(), {
  showQuantities: false,
  showSaveLabel: false,
  showModelName: false,
  showSaveInHeader: false,
  cardWidth: 'md'
})

const color = computed(() => props.color || props.colour || '')
const cart = useCartStore()
cart.hydrate()
const settings = useSettingsStore()
settings.hydrate()

const workbookMode = computed(() => settings.workbookMode || 'All')

const imageSrc = computed(() => imageForColour(props.model, color.value))

const productLink = computed(() => {
  // Bara "All" skall inte ha "?category=All"
  if (workbookMode.value === 'All' || !workbookMode.value) {
    return `/products/${props.model.ProductFamilySlug}#style-${props.model.Style}`
  }
  return `/products/${props.model.ProductFamilySlug}?category=${encodeURIComponent(workbookMode.value.toLowerCase())}#style-${props.model.Style}`
})

const isNewColour = computed(() => {
  return props.model.variants.some(v => v.Colour === color.value && v.NewColour)
})

const sizeRows = computed(() => {
  if (!props.showQuantities) return []
  const rows: { sku: string; displaySize: string; qty: number }[] = []
  for (const v of props.model.variants) {
    if (v.Colour !== color.value) continue
    if (!v.SKU) continue
    const code = normalizeSizeCode(v.SizeCode || v.Size || '')
    const li = cart.lineItems[v.SKU]
    rows.push({
      sku: v.SKU,
      displaySize: code,
      qty: li ? li.qty : 0
    })
  }
  rows.sort((a, b) => {
    const ai = CANONICAL_ORDER.indexOf(a.displaySize as Canon)
    const bi = CANONICAL_ORDER.indexOf(b.displaySize as Canon)
    if (ai !== bi) return ai - bi
    return a.displaySize.localeCompare(b.displaySize)
  })
  return rows
})

const hasAnyQty = computed(() => sizeRows.value.some(r => r.qty > 0))

function inc(sku: string) { cart.inc(sku) }
function dec(sku: string) { cart.dec(sku) }
function clearVariant(sku: string) { cart.clearVariant(sku) }
function removeColor() { cart.clearColour(props.model.Style, color.value) }
function onImgError(e: Event) {
  (e.target as HTMLImageElement).src = '/images/placeholder.png'
}

// -------- Default QTY Settings Actions --------
function incDefaultSize(size: string) {
  const current = settings.defaultSizeQuantities[size] ?? 0
  settings.setDefaultSizeQty(size, current + 1)
}
function decDefaultSize(size: string) {
  const current = settings.defaultSizeQuantities[size] ?? 0
  if (current > 0) settings.setDefaultSizeQty(size, current - 1)
}
function resetQuantityDefaults() {
  settings.resetDefaultSizeQuantities()
}

// Sätt QTY för alla varianter i aktuell färg enligt defaultSizeQuantities
function applyDefaultQuantities() {
  for (const v of props.model.variants) {
    if (v.Colour !== color.value) continue
    if (!v.SKU) continue
    const code = normalizeSizeCode(v.SizeCode || v.Size || '')
    const defaultQty = settings.defaultSizeQuantities[code] ?? null
    if (defaultQty != null && typeof defaultQty === 'number') {
      cart.setVariantQty(v.SKU, defaultQty)
    }
  }
}

// --- Input field logic for qty ---
function onQtyInput(sku: string, value: string) {
  let qty = parseInt(value.replace(/[^0-9]/g, ''), 10)
  if (isNaN(qty) || qty < 0) qty = 0
  if (qty > 999) qty = 999
  cart.setVariantQty(sku, qty)
}
function onQtyBlur(sku: string, value: string) {
  // Ensure value is an integer and not empty
  let qty = parseInt(value.replace(/[^0-9]/g, ''), 10)
  if (isNaN(qty) || qty < 0) qty = 0
  if (qty > 999) qty = 999
  cart.setVariantQty(sku, qty)
}

// Size normalizer (keep in sync with LineOverview.vue)
function normalizeSizeCode(raw: string): string {
  if (!raw) return ''
  const s = raw.trim().toUpperCase()
  if (['ONE SIZE', 'OS', 'O/S'].includes(s)) return 'One Size'
  if (s === 'XS') return 'XS'
  if (s === 'S' || s === 'SM') return 'SM'
  if (s === 'M' || s === 'MD') return 'MD'
  if (s === 'L' || s === 'LG') return 'LG'
  if (s === 'XL') return 'XL'
  if (s === 'XXL' || s === '2XL') return 'XXL'
  if (s === 'XXXL' || s === '3XL') return 'XXXL'
  return raw.trim()
}
</script>

<style scoped>
.workbook-color-card img {
  aspect-ratio: 3/4;
}
input[type="number"] {
  -moz-appearance: textfield;
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
